import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * A message bus between the page and the BuniAI widget.
 *
 * Anything on the site can ask to open the chat, or open it with a question
 * already asked. BuniChat.js owns the widget and does the actual work — a
 * question is held here until the widget reports it is connected, because the
 * chat drops messages sent while it is still handshaking.
 */
const ChatContext = createContext({
  isReady: false,
  isOpen: false,
  hasOpened: false,
  openRequest: 0,
  pendingQuestion: null,
  ask: () => {},
  openChat: () => {},
  clearPending: () => {},
  reportOpen: () => {},
  reportReady: () => {},
});

export function ChatProvider({ children }) {
  const [isReady, setIsReady] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [openRequest, setOpenRequest] = useState(0);
  const [pendingQuestion, setPendingQuestion] = useState(null);

  const openChat = useCallback(() => setOpenRequest((n) => n + 1), []);

  const ask = useCallback((question) => {
    const trimmed = typeof question === 'string' ? question.trim() : '';
    if (trimmed) setPendingQuestion(trimmed);
    setOpenRequest((n) => n + 1);
  }, []);

  const clearPending = useCallback(() => setPendingQuestion(null), []);

  const reportOpen = useCallback((open) => {
    setIsOpen(open);
    if (open) setHasOpened(true);
  }, []);

  const reportReady = useCallback((ready) => setIsReady(ready), []);

  const value = useMemo(
    () => ({
      isReady,
      isOpen,
      hasOpened,
      openRequest,
      pendingQuestion,
      ask,
      openChat,
      clearPending,
      reportOpen,
      reportReady,
    }),
    [isReady, isOpen, hasOpened, openRequest, pendingQuestion, ask, openChat, clearPending, reportOpen, reportReady],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export const useSiteChat = () => useContext(ChatContext);
